import React, { ReactNode } from "react"
import clsx from "clsx"
import { NavLink } from "react-router-dom"
import { useTheme } from "styled-components"
import { Row } from "ui/components/atoms/_atoms"
import type { StyledButtonProps } from "ui/components/atoms/button"
import styles from "ui/components/atoms/button/Button.module.css"
import Icon from "ui/components/atoms/icon"
import type { colorKeyType } from "ui/theme"
import { baseTheme } from "ui/theme"
import { generateStylesFromProps } from "ui/utils/styles"

export type LinkProps = StyledButtonProps & {
  children: ReactNode
  $type?: "primary" | "secondary" | "third" | "fourth"
  $activeBackground?: colorKeyType
  end?: boolean
  icon?: string
  to: string
  target?: string
}

const Link = ({
  children,
  to,
  $type = "primary",
  icon,
  disabled,
  $padding = "12px 20px",
  $hoverColor,
  $hoverBackground,
  $boxShadow = false,
  className = "",
  $background,
  style = {},
  $activeBackground = "primary",
  $justifyContent,
  end,
  target,
  ...rest
}: LinkProps & { className?: string; style?: React.CSSProperties }) => {
  const theme = useTheme() as typeof baseTheme
  const generatedStyles = generateStylesFromProps({ ...rest, $padding, $background })

  const linkClassName = clsx(
    styles.button,
    styles[$type],
    {
      [styles.boxShadow]: $boxShadow,
    },
    className,
  )

  const iconMarginSize = $type === "third" || $type === "fourth" ? "XS" : "20px"

  return (
    <NavLink
      to={to}
      end={end}
      target={target}
      className={linkClassName}
      style={({ isActive }) => ({
        ...generatedStyles,
        ...style,
        ...(disabled ? { opacity: 0.5, pointerEvents: "none" as const } : {}),
        ...(isActive
          ? {
              color: theme.color.white,
              background: theme.color[$activeBackground],
            }
          : {}),
      })}
      data-hover-background={$hoverBackground}
      data-hover-color={$hoverColor}
      onClick={e => {
        if (disabled) e.preventDefault()
      }}
      aria-disabled={disabled || undefined}
      tabIndex={disabled ? -1 : undefined}
    >
      <Row $maxWidth="100%" $alignItems="center" $margin="0 auto">
        {icon && <Icon icon={icon} $marginRight={iconMarginSize} />}
        <span className={styles.span}>{children}</span>
      </Row>
    </NavLink>
  )
}

export default Link
